import { memo, useState, useCallback, useMemo } from 'react';
import { RotateCcw, Maximize2, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';
import { useQuizTheme, answerTheme, marqueSigne } from './quizTheme';
import PleinEcranInteractif from './PleinEcranInteractif';
import { etatsPoints } from './corrige';
import { planEtiquettes } from '../../../utils/repereEtiquettes';
import {
    cadreRepere, decorSegments, segmentsRelies, flechePx, directionsDecor, directionsPolygone, etiquetteDirigee,
} from '../../../utils/repereDecor';

/**
 * Repère orthogonal interactif — l'élève place des points. Quadrant 1 par défaut
 * (origine en (0;0)) ; xMin/yMin négatifs optionnels étendent aux 4 quadrants
 * (repérage 5e : coordonnées relatives). Les axes passent TOUJOURS par l'origine
 * (contrat : xMin ≤ 0 ≤ xMax, yMin ≤ 0 ≤ yMax, bornes multiples du pas).
 *
 * Jumeau 2D de NumberLineInteractiveEleve : tap (clic) sur le quadrillage pour
 * placer le point actif (snap sur les graduations → réponse non ambiguë),
 * avancement séquentiel au tap, plein écran avec réglage fin directionnel.
 *
 * Format de réponse émis : {"A":"2;8","B":"5;20"} — "x;y" SANS espaces, décimaux
 * en virgule française. La bonne_reponse du générateur DOIT suivre exactement le
 * même format canonique (comparaison JSON stricte côté scoring).
 *
 * @param {Object} repere - Config { xMin?, yMin?, xMax, yMax, xStep, yStep, xLabel, yLabel, grille }
 * @param {Array<string>} pointsToPlace - Labels à placer (ex: ["A", "B"] ou ["2 kg", "5 kg"])
 * @param {Array<{x,y,label}>} dejaPlaces - Points statiques affichés d'office (optionnel)
 * @param {string} selectedValue - Réponse existante (JSON string, restauration navigation)
 * @param {Function} onAnswer - Callback (jsonString) à chaque changement
 * @param {Function} onComplete - Callback (bool) quand tous les points sont placés
 */

// "2,5;12" → {x: 2.5, y: 12} (null si non parsable)
const parseXY = (str) => {
    const parts = String(str).split(';');
    if (parts.length !== 2) return null;
    const x = parseFloat(parts[0].replace(',', '.'));
    const y = parseFloat(parts[1].replace(',', '.'));
    if (Number.isNaN(x) || Number.isNaN(y)) return null;
    return { x, y };
};

// Relit une réponse JSON ('{"A":"2;8"}') et reconvertit en coordonnées numériques.
const parsePlacedPoints = (json) => {
    try {
        const obj = JSON.parse(json);
        const out = {};
        Object.entries(obj).forEach(([label, v]) => {
            const xy = parseXY(v);
            if (xy) out[label] = xy;
        });
        return out;
    } catch { return {}; }
};

const fmtFr = (val) => String(Math.round(val * 1000) / 1000).replace('.', ',');

const COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899'];
// Correction : les tons des classes juste / faux d'answerTheme (les couleurs
// de points ci-dessus comptent un rouge et un vert, elles s'effacent alors).
const CORRECTION = { juste: '#10b981', faux: '#ef4444' };

// Tailles selon le mode (normal vs plein écran) — même viewBox, typo/rayons adaptés
const SIZES = {
    normal: { r: 5, txt: 10, lbl: 11, axis: 11, line: 1.5 },
    big: { r: 9, txt: 15, lbl: 17, axis: 15, line: 2 },
};

/**
 * @param {boolean} masquerCoordonnees - quand vrai, le point fantôme sous le curseur reste
 *   mais ses coordonnées « (x ; y) » ne sont pas affichées : sur les exercices où lire ou
 *   placer des coordonnées EST le savoir évalué, ce texte donnait la réponse (demande prof
 *   2026-09-18). Porté par contenu.masquerCoordonnees (cf. FORMATS_TANOED §9bis).
 * @param {?{polygones?: string[][], fleches?: string[][]}} decor - figures et flèches
 *   dessinées d'office entre points de dejaPlaces (contenu.decor, 2026-09-24).
 * @param {?string[]} relier - points à placer reliés dans l'ordre au fur et à mesure,
 *   la figure fermée au dernier (contenu.relier, 2026-09-24). En correction, la
 *   figure attendue en pointillé vert si un de ses sommets est faux.
 * repere.quadrillage (2026-09-24) : carreaux carrés, ni axes, ni graduations, ni
 *   coordonnées sous le curseur (utils/repereDecor.js).
 */
const Repere2DInteractiveEleve = memo(function Repere2DInteractiveEleve({
    repere, pointsToPlace = [], dejaPlaces = [], selectedValue, onAnswer, onComplete,
    masquerCoordonnees = false, corrige = null, decor = null, relier = null,
}) {
    const clair = useQuizTheme() === 'clair';
    const t = answerTheme(clair);

    // ─── Correction sur place (lot 6, 2026-09-21) ───
    // Même mécanique que la droite graduée (NumberLineInteractiveEleve) : ses
    // points en vert ou en rouge, la position attendue d'un point faux en
    // pointillé vert avec son nom, rien de plus (un trait qui reliait le sien à
    // la bonne place prêtait à confusion, retiré à l'écran, constat prof
    // 2026-09-21). Pastilles avec coche ou croix, figure figée, aides masquées.
    // Ce qui s'affiche est ce que Quiz a jugé. Sans `corrige`, le rendu d'avant.
    const etats = useMemo(() => (corrige ? etatsPoints(corrige) : null), [corrige]);
    const pointsCorriges = useMemo(() => (corrige ? parsePlacedPoints(corrige.reponseEleve) : null), [corrige]);
    const {
        xMin = 0, yMin = 0, xMax = 10, yMax = 10, xStep = 1, yStep = 1,
        xLabel = '', yLabel = '', grille = true,
    } = repere || {};

    // ─── Points placés : { label → {x, y} } ───
    const [placedPoints, setPlacedPoints] = useState(() => (
        selectedValue ? parsePlacedPoints(selectedValue) : {}
    ));

    // Label actif — initialisé au premier point non placé (au montage uniquement).
    // ⚠ PAS d'auto-avancement réactif : l'avancement séquentiel ne se fait qu'au TAP
    // (sinon le réglage fin ferait sauter au point suivant).
    const [activeLabel, setActiveLabel] = useState(() => {
        const initial = selectedValue ? parsePlacedPoints(selectedValue) : {};
        return pointsToPlace.find(l => initial[l] === undefined) || null;
    });

    // Coordonnées snappées sous le curseur — point fantôme de prévisualisation
    const [hoverXY, setHoverXY] = useState(null);

    const [isExpanded, setIsExpanded] = useState(false);

    // ─── Géométrie SVG (viewBox fixe, responsive via width 100%) ───
    // Repère : 600 × 412 ; quadrillage : carreaux carrés, sans axes (utils/repereDecor).
    const { W, H, padL, padT, plotW, plotH, quadrillage: quad } = useMemo(() => cadreRepere(repere), [repere]);
    const decorSegs = useMemo(() => decorSegments(decor, dejaPlaces), [decor, dejaPlaces]);
    const dirsDecor = useMemo(() => directionsDecor(decor, dejaPlaces), [decor, dejaPlaces]);

    const toPx = useCallback((x, y) => ({
        px: padL + ((x - xMin) / (xMax - xMin)) * plotW,
        py: padT + plotH - ((y - yMin) / (yMax - yMin)) * plotH,
    }), [xMin, xMax, yMin, yMax, padL, padT, plotW, plotH]);

    const snap = useCallback((val, step, min, max) => {
        const snapped = Math.round(val / step) * step;
        return Math.max(min, Math.min(max, Math.round(snapped * 1000) / 1000));
    }, []);

    // Graduations (garde-fou : ≤ 200 pas) + densité de labels (≤ ~9 par axe)
    const makeGrads = (min, max, step) => {
        if (step <= 0 || max <= min) return [];
        const grads = [];
        for (let v = min; v <= max + step / 2 && grads.length <= 200; v += step) {
            grads.push(Math.round(v * 1000) / 1000);
        }
        return grads;
    };
    const xGrads = useMemo(() => makeGrads(xMin, xMax, xStep), [xMin, xMax, xStep]);
    const yGrads = useMemo(() => makeGrads(yMin, yMax, yStep), [yMin, yMax, yStep]);
    const xEtiq = useMemo(() => planEtiquettes(xGrads, xStep, xMin, xMax, plotW / (xMax - xMin), 22), [xGrads, xStep, xMin, xMax, plotW]);
    const yEtiq = useMemo(() => planEtiquettes(yGrads, yStep, yMin, yMax, plotH / (yMax - yMin), 14), [yGrads, yStep, yMin, yMax, plotH]);

    // ─── Émettre la réponse (format canonique : {"A":"2;8"} virgule FR, sans espaces) ───
    const emitAnswer = useCallback((points) => {
        const obj = {};
        Object.entries(points).forEach(([label, xy]) => {
            obj[label] = `${fmtFr(xy.x)};${fmtFr(xy.y)}`;
        });
        onAnswer(JSON.stringify(obj));
        onComplete(pointsToPlace.every(l => points[l] !== undefined));
    }, [pointsToPlace, onAnswer, onComplete]);

    // Position pixel d'un événement → coordonnées snappées (null hors zone utile élargie)
    const xyFromEvent = useCallback((e) => {
        const svg = e.currentTarget;
        if (!svg) return null;
        const rect = svg.getBoundingClientRect();
        if (!rect.width) return null;
        const scale = W / rect.width;
        const px = (e.clientX - rect.left) * scale;
        const py = (e.clientY - rect.top) * scale;
        const x = xMin + ((px - padL) / plotW) * (xMax - xMin);
        const y = yMin + ((padT + plotH - py) / plotH) * (yMax - yMin);
        return { x: snap(x, xStep, xMin, xMax), y: snap(y, yStep, yMin, yMax) };
    }, [W, padL, padT, plotW, plotH, xMin, xMax, yMin, yMax, xStep, yStep, snap]);

    // ─── Clic = placer le point actif ───
    const handleSvgClick = useCallback((e) => {
        if (!activeLabel) return;
        const xy = xyFromEvent(e);
        if (xy === null) return;
        setHoverXY(null);
        const next = { ...placedPoints, [activeLabel]: xy };
        setPlacedPoints(next);
        emitAnswer(next);
        const nextUnplaced = pointsToPlace.find(l => next[l] === undefined);
        setActiveLabel(nextUnplaced || activeLabel);
    }, [activeLabel, placedPoints, pointsToPlace, xyFromEvent, emitAnswer]);

    const handleSvgMove = useCallback((e) => {
        if (!activeLabel) return;
        setHoverXY(xyFromEvent(e));
    }, [activeLabel, xyFromEvent]);

    const handleSvgLeave = useCallback(() => setHoverXY(null), []);

    // ─── Réglage fin directionnel (plein écran) : décale le point actif d'un pas ───
    // ⚠ emitAnswer HORS de l'updater setState (effet de bord).
    const nudge = useCallback((dx, dy) => {
        if (!activeLabel) return;
        const base = placedPoints[activeLabel] || { x: 0, y: 0 };
        const next = {
            ...placedPoints,
            [activeLabel]: {
                x: snap(base.x + dx * xStep, xStep, xMin, xMax),
                y: snap(base.y + dy * yStep, yStep, yMin, yMax),
            },
        };
        setPlacedPoints(next);
        emitAnswer(next);
    }, [activeLabel, placedPoints, xStep, yStep, xMin, xMax, yMin, yMax, snap, emitAnswer]);

    const handleReset = useCallback(() => {
        setPlacedPoints({});
        setHoverXY(null);
        setActiveLabel(pointsToPlace[0] || null);
        emitAnswer({});
    }, [pointsToPlace, emitAnswer]);

    // Sélectionner un label (sans l'effacer) pour l'ajuster / le replacer
    const handleLabelClick = useCallback((label) => setActiveLabel(label), []);

    const lineColor = clair ? '#475569' : '#94a3b8';
    const gridColor = clair ? '#e2e8f0' : '#334155';
    const textColor = clair ? '#1e293b' : '#cbd5e1';
    // Décor : la figure donnée, la flèche (vert), la figure à construire (bleu, neutre en correction)
    const figureColor = clair ? '#334155' : '#cbd5e1';
    const flecheColor = clair ? '#16a34a' : '#4ade80';
    const imageColor = COLORS[0];
    const imageCorrigeeColor = clair ? '#64748b' : '#94a3b8';

    // ─── Pills de labels (réutilisé vue normale + plein écran) ───
    const renderPills = (dark = false) => (
        <div className="flex flex-wrap gap-2">
            {pointsToPlace.map((label, i) => {
                const isPlaced = placedPoints[label] !== undefined;
                const isActive = activeLabel === label;
                const color = COLORS[i % COLORS.length];
                // En correction : la pastille porte le verdict de son point
                const e = etats ? etats.get(label) : null;
                const etat = e ? (e.juste ? 'juste' : 'faux') : null;
                const cls = etat
                    ? `border-2 ${t[etat]} cursor-default`
                    : isActive
                        ? (dark || !clair ? 'ring-2 ring-offset-2 ring-blue-400 bg-slate-700 text-white ring-offset-slate-900' : 'ring-2 ring-offset-2 ring-blue-400 bg-blue-50 text-blue-700')
                        : isPlaced
                            ? (dark || !clair ? 'bg-slate-700/50 text-slate-300 border border-slate-600' : 'bg-slate-100 text-slate-700 border border-slate-300')
                            : (dark || !clair ? 'bg-slate-700/30 text-slate-400 border border-dashed border-slate-600' : 'bg-white text-slate-500 border border-dashed border-slate-300');
                return (
                    <button
                        key={label}
                        onClick={() => handleLabelClick(label)}
                        disabled={Boolean(etat)}
                        data-etat={etat || undefined}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all ${cls}`}
                    >
                        <span className="w-3 h-3 rounded-full" style={{ background: etat ? CORRECTION[etat] : color }} />
                        {label}
                        {etat ? <span aria-hidden="true" className="ml-1 font-black">{marqueSigne(etat)}</span> : (isPlaced && <span className="text-[10px] text-emerald-400 ml-1">✓</span>)}
                    </button>
                );
            })}
            {!etats && Object.keys(placedPoints).length > 0 && (
                <button
                    onClick={handleReset}
                    className={`ml-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition ${dark || !clair ? 'text-slate-400 border-slate-600 hover:bg-slate-700 hover:text-slate-200' : 'text-slate-500 border-slate-300 hover:bg-slate-100 hover:text-slate-700'}`}
                    title="Effacer tous les points et recommencer"
                >
                    <RotateCcw size={12} /> Réinitialiser
                </button>
            )}
        </div>
    );

    // ─── Rendu du repère SVG (paramétré normal/plein écran) ───
    const renderRepere = (big) => {
        const s = big ? SIZES.big : SIZES.normal;
        const origin = toPx(0, 0);
        // En correction, la figure ne réagit plus
        const handlers = etats ? {} : {
            onClick: handleSvgClick,
            onPointerMove: handleSvgMove,
            onPointerLeave: handleSvgLeave,
        };
        const pts = pointsCorriges || placedPoints;
        const dirsRelier = directionsPolygone(relier, pts);

        return (
            <svg
                viewBox={`0 0 ${W} ${H}`}
                className="w-full select-none"
                preserveAspectRatio="xMidYMid meet"
                {...handlers}
            >
                {/* Zone de clic (tout le cadran, curseur croix) */}
                {activeLabel && !etats && (
                    <rect
                        x={padL - 8} y={padT - 8} width={plotW + 16} height={plotH + 16}
                        fill="transparent" className="cursor-crosshair"
                    />
                )}

                {/* Quadrillage (la ligne 0 est portée par les axes, sauf en quadrillage sans axes) */}
                {grille && xGrads.map((v, i) => (quad || v !== 0) && (
                    <line
                        key={`gx-${i}`} pointerEvents="none"
                        x1={toPx(v, 0).px} y1={padT} x2={toPx(v, 0).px} y2={padT + plotH}
                        stroke={gridColor} strokeWidth={0.75}
                    />
                ))}
                {grille && yGrads.map((v, i) => (quad || v !== 0) && (
                    <line
                        key={`gy-${i}`} pointerEvents="none"
                        x1={padL} y1={toPx(0, v).py} x2={padL + plotW} y2={toPx(0, v).py}
                        stroke={gridColor} strokeWidth={0.75}
                    />
                ))}

                {/* Axes + flèches (toujours par l'origine — xMin/yMin ≤ 0) ; aucun en quadrillage */}
                {quad ? null : (
                <g pointerEvents="none">
                    <line x1={padL} y1={origin.py} x2={padL + plotW + 12} y2={origin.py} stroke={lineColor} strokeWidth={s.line} />
                    <polygon points={`${padL + plotW + 18},${origin.py} ${padL + plotW + 9},${origin.py - 4} ${padL + plotW + 9},${origin.py + 4}`} fill={lineColor} />
                    <line x1={origin.px} y1={padT + plotH} x2={origin.px} y2={padT - 12} stroke={lineColor} strokeWidth={s.line} />
                    <polygon points={`${origin.px},${padT - 18} ${origin.px - 4},${padT - 9} ${origin.px + 4},${padT - 9}`} fill={lineColor} />

                    {/* Graduations + valeurs (le 0 est décalé à gauche de l'axe des ordonnées) */}
                    {xGrads.map((v, i) => {
                        const { px } = toPx(v, 0);
                        const estOrigine = v === 0 && yMin < 0;
                        return (
                            <g key={`tx-${i}`}>
                                <line x1={px} y1={origin.py - 4} x2={px} y2={origin.py + 4} stroke={lineColor} strokeWidth={1} />
                                {xEtiq[i] > 0 && (
                                    <text
                                        x={estOrigine ? px - 6 : px}
                                        y={origin.py + s.txt + 8}
                                        textAnchor={estOrigine ? 'end' : 'middle'}
                                        fill={textColor} style={{ fontSize: s.txt * xEtiq[i] }}
                                    >
                                        {fmtFr(v)}
                                    </text>
                                )}
                            </g>
                        );
                    })}
                    {yGrads.map((v, i) => {
                        if (v === 0) return null; // le 0 est porté par l'axe des abscisses
                        const { py } = toPx(0, v);
                        return (
                            <g key={`ty-${i}`}>
                                <line x1={origin.px - 4} y1={py} x2={origin.px + 4} y2={py} stroke={lineColor} strokeWidth={1} />
                                {yEtiq[i] > 0 && (
                                    <text x={origin.px - 8} y={py + s.txt / 3} textAnchor="end" fill={textColor} style={{ fontSize: s.txt * yEtiq[i] }}>
                                        {fmtFr(v)}
                                    </text>
                                )}
                            </g>
                        );
                    })}

                    {/* Noms des axes (grandeurs + unités) */}
                    {xLabel && (
                        <text x={padL + plotW + 16} y={origin.py + s.axis + 16} textAnchor="end" fill={textColor} style={{ fontSize: s.axis }} className="font-medium">
                            {xLabel}
                        </text>
                    )}
                    {yLabel && (
                        <text x={origin.px + 8} y={padT - 10} textAnchor="start" fill={textColor} style={{ fontSize: s.axis }} className="font-medium">
                            {yLabel}
                        </text>
                    )}
                </g>
                )}

                {/* Décor : la figure donnée et la flèche (contenu.decor) */}
                {decorSegs.cotes.length || decorSegs.fleches.length ? (
                    <g pointerEvents="none" data-testid="decor">
                        {decorSegs.cotes.map((c, i) => {
                            const a = toPx(c.x1, c.y1); const b = toPx(c.x2, c.y2);
                            return <line key={`dc-${i}`} x1={a.px} y1={a.py} x2={b.px} y2={b.py} stroke={figureColor} strokeWidth={s.line + 0.5} strokeLinecap="round" />;
                        })}
                        {decorSegs.fleches.map((f, i) => {
                            const a = toPx(f.x1, f.y1); const b = toPx(f.x2, f.y2);
                            const fl = flechePx(a.px, a.py, b.px, b.py, { taille: big ? 16 : 12, retrait: s.r + 1 });
                            return (
                                <g key={`df-${i}`} data-testid="fleche">
                                    <line x1={fl.trait.x1} y1={fl.trait.y1} x2={fl.trait.x2} y2={fl.trait.y2} stroke={flecheColor} strokeWidth={s.line + 1} strokeLinecap="round" />
                                    <polygon points={fl.pointe} fill={flecheColor} />
                                </g>
                            );
                        })}
                    </g>
                ) : null}

                {/* La figure à construire : ses sommets placés, reliés (contenu.relier) */}
                {(() => {
                    const segs = segmentsRelies(relier, pts);
                    if (!segs.length) return null;
                    return (
                        <g pointerEvents="none" data-testid="relier">
                            {segs.map((c, i) => {
                                const a = toPx(c.x1, c.y1); const b = toPx(c.x2, c.y2);
                                return <line key={`rl-${i}`} x1={a.px} y1={a.py} x2={b.px} y2={b.py} stroke={etats ? imageCorrigeeColor : imageColor} strokeWidth={s.line + 0.5} strokeLinecap="round" />;
                            })}
                        </g>
                    );
                })()}

                {/* Correction : la figure attendue en pointillé vert, si un de ses sommets est faux */}
                {etats && Array.isArray(relier) && relier.some((l) => etats.get(l) && !etats.get(l).juste) ? (() => {
                    const attendus = {};
                    relier.forEach((l) => { const xy = etats.get(l) ? parseXY(etats.get(l).attendu) : null; if (xy) attendus[l] = xy; });
                    return (
                        <g pointerEvents="none" data-testid="relier-attendu">
                            {segmentsRelies(relier, attendus).map((c, i) => {
                                const a = toPx(c.x1, c.y1); const b = toPx(c.x2, c.y2);
                                return <line key={`ra-${i}`} x1={a.px} y1={a.py} x2={b.px} y2={b.py} stroke={CORRECTION.juste} strokeWidth={s.line + 0.5} strokeDasharray="6 4" />;
                            })}
                        </g>
                    );
                })() : null}

                {/* Points statiques (dejaPlaces) */}
                {dejaPlaces.map((p, i) => {
                    const { px, py } = toPx(p.x, p.y);
                    const gray = clair ? '#64748b' : '#94a3b8';
                    return (
                        <g key={`static-${i}`} pointerEvents="none">
                            <circle cx={px} cy={py} r={s.r - 1} fill={gray} stroke="white" strokeWidth={1} />
                            {p.label && (dirsDecor[p.label] ? (() => {
                                // Point du décor : son nom hors de la figure, ou hors de la flèche (2026-09-24)
                                const e = etiquetteDirigee(px, py, dirsDecor[p.label], s.r + 8, s.lbl);
                                return <text x={e.x} y={e.y} textAnchor={e.ancre} fill={gray} style={{ fontSize: s.lbl }} className="font-bold">{p.label}</text>;
                            })() : (
                                // Étiquette à droite du point (revue 2026-08-13) : au-dessus,
                                // elle se confondait avec les graduations. Exception : un point
                                // SUR l'axe horizontal garde l'étiquette au-dessus, sinon elle
                                // serait posée sur le trait de l'axe.
                                p.y === 0 ? (
                                    <text x={px} y={py - s.r - 5} textAnchor="middle" fill={gray} style={{ fontSize: s.lbl }} className="font-bold">
                                        {p.label}
                                    </text>
                                ) : (
                                    <text x={px + s.r + 4} y={py + s.lbl * 0.35} textAnchor="start" fill={gray} style={{ fontSize: s.lbl }} className="font-bold">
                                        {p.label}
                                    </text>
                                )
                            ))}
                        </g>
                    );
                })}

                {/* Points placés (guides pointillés vers les axes sur le point actif ; en correction : la couleur du verdict) */}
                {pointsToPlace.map((label, i) => {
                    const xy = pts[label];
                    if (xy === undefined) return null;
                    const { px, py } = toPx(xy.x, xy.y);
                    const e = etats ? etats.get(label) : null;
                    const color = e ? CORRECTION[e.juste ? 'juste' : 'faux'] : COLORS[i % COLORS.length];
                    const isActive = activeLabel === label && !etats;
                    return (
                        <g key={`point-${label}`} pointerEvents="none">
                            {isActive && (
                                <>
                                    {quad ? null : (
                                        <>
                                            <line x1={px} y1={py} x2={px} y2={origin.py} stroke={color} strokeWidth={1} strokeDasharray="3 3" opacity={0.5} />
                                            <line x1={px} y1={py} x2={origin.px} y2={py} stroke={color} strokeWidth={1} strokeDasharray="3 3" opacity={0.5} />
                                        </>
                                    )}
                                    {big && <circle cx={px} cy={py} r={s.r + 7} fill={color} opacity={0.18} />}
                                </>
                            )}
                            <circle cx={px} cy={py} r={s.r} fill={color} stroke="white" strokeWidth={big ? 2.5 : 1.5} />
                            {dirsRelier[label] ? (() => {
                                // Sommet de la figure construite (tous placés) : son nom vers l'extérieur
                                const e = etiquetteDirigee(px, py, dirsRelier[label], s.r + 8, s.lbl);
                                return <text x={e.x} y={e.y} textAnchor={e.ancre} fill={color} style={{ fontSize: s.lbl }} className="font-bold">{label}</text>;
                            })() : xy.y === 0 ? (
                                <text x={px} y={py - s.r - 5} textAnchor="middle" fill={color} style={{ fontSize: s.lbl }} className="font-bold">
                                    {label}
                                </text>
                            ) : (
                                <text x={px + s.r + 4} y={py + s.lbl * 0.35} textAnchor="start" fill={color} style={{ fontSize: s.lbl }} className="font-bold">
                                    {label}
                                </text>
                            )}
                        </g>
                    );
                })}

                {/* Correction : la position attendue d'un point faux, en pointillé, nommée */}
                {etats ? pointsToPlace.map((label) => {
                    const e = etats.get(label);
                    if (!e || e.juste) return null;
                    const xy = parseXY(e.attendu);
                    if (!xy) return null;
                    const { px, py } = toPx(xy.x, xy.y);
                    return (
                        <g key={`attendu-${label}`} pointerEvents="none" data-testid={`attendu-${label}`}>
                            <circle cx={px} cy={py} r={s.r + 1} fill="none" stroke={CORRECTION.juste} strokeWidth={2} strokeDasharray="3 2" />
                            {xy.y === 0 ? (
                                <text x={px} y={py - s.r - 5} textAnchor="middle" fill={CORRECTION.juste} style={{ fontSize: s.lbl }} className="font-bold">{label}</text>
                            ) : (
                                <text x={px + s.r + 4} y={py + s.lbl * 0.35} textAnchor="start" fill={CORRECTION.juste} style={{ fontSize: s.lbl }} className="font-bold">{label}</text>
                            )}
                        </g>
                    );
                }) : null}

                {/* Point fantôme + coordonnées snappées */}
                {activeLabel && !etats && hoverXY !== null && (() => {
                    const ghostColor = COLORS[Math.max(0, pointsToPlace.indexOf(activeLabel)) % COLORS.length];
                    const { px, py } = toPx(hoverXY.x, hoverXY.y);
                    const coordTxt = `(${fmtFr(hoverXY.x)} ; ${fmtFr(hoverXY.y)})`;
                    const txtAnchor = px > padL + plotW - 70 ? 'end' : 'start';
                    return (
                        <g pointerEvents="none" opacity={0.75}>
                            <circle cx={px} cy={py} r={s.r} fill={ghostColor} stroke="white" strokeWidth={1.5} strokeDasharray="2 2" opacity={0.6} />
                            {masquerCoordonnees || quad ? null : (
                                <text x={px + (txtAnchor === 'start' ? 10 : -10)} y={py - 8} textAnchor={txtAnchor} fill={ghostColor} style={{ fontSize: s.txt + 1 }} className="font-semibold">
                                    {coordTxt}
                                </text>
                            )}
                        </g>
                    );
                })()}
            </svg>
        );
    };

    return (
        <div className="space-y-3">
            {etats ? null : (
            <p className={`text-xs sm:text-sm font-medium ${t.instructions} flex items-center gap-2`}>
                <span>📍</span> Clique sur un label puis sur le quadrillage pour placer le point !
            </p>
            )}

            {renderPills(false)}

            {etats ? null : (
            <button
                type="button"
                onClick={() => setIsExpanded(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition ${clair ? 'text-blue-700 border-blue-300 bg-blue-50 hover:bg-blue-100' : 'text-blue-200 border-blue-500/40 bg-blue-500/10 hover:bg-blue-500/20'}`}
            >
                <Maximize2 size={14} /> Agrandir pour placer
            </button>
            )}

            {/* Repère — vue normale */}
            <div
                className={`w-full rounded-xl border-2 transition-all ${clair ? 'bg-white' : ''} ${
                    activeLabel && !etats ? 'border-blue-500/50 cursor-crosshair' : (clair ? 'border-slate-300' : 'border-slate-600')
                }`}
            >
                {renderRepere(false)}
            </div>

            {/* Vue plein écran : tap + réglage fin directionnel */}
            <PleinEcranInteractif open={isExpanded} onClose={() => setIsExpanded(false)} title="Place les points dans le repère">
                <div className="max-w-3xl mx-auto space-y-4">
                    <p className="text-slate-300 text-sm flex items-center gap-2">
                        <span>📍</span> Choisis un label, puis <b className="text-white">tape</b> sur le quadrillage (ou ajuste avec les flèches).
                    </p>

                    {renderPills(true)}

                    <div className="w-full rounded-xl border-2 border-slate-300 bg-white">
                        {renderRepere(true)}
                    </div>

                    {/* Réglage fin du point actif (un pas de graduation par flèche) */}
                    <div className="flex items-center justify-center gap-3">
                        {[
                            { dx: -1, dy: 0, Icon: ChevronLeft, aria: 'Décaler le point vers la gauche' },
                            { dx: 0, dy: -1, Icon: ChevronDown, aria: 'Décaler le point vers le bas' },
                            { dx: 0, dy: 1, Icon: ChevronUp, aria: 'Décaler le point vers le haut' },
                            { dx: 1, dy: 0, Icon: ChevronRight, aria: 'Décaler le point vers la droite' },
                        ].map(({ dx, dy, Icon, aria }) => (
                            <button
                                key={aria}
                                type="button"
                                onClick={() => nudge(dx, dy)}
                                disabled={!activeLabel}
                                aria-label={aria}
                                className="w-14 h-14 rounded-2xl bg-slate-800 hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-white border border-slate-600 transition"
                            >
                                <Icon className="w-6 h-6" />
                            </button>
                        ))}
                        <span className="text-slate-300 text-sm font-medium min-w-[7rem] text-center">
                            {activeLabel ? <>Ajuste le point <b className="text-white">{activeLabel}</b></> : 'Tous placés ✓'}
                        </span>
                    </div>

                    <button
                        type="button"
                        onClick={() => setIsExpanded(false)}
                        className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 transition shadow-lg shadow-blue-500/30"
                    >
                        Terminé
                    </button>
                </div>
            </PleinEcranInteractif>
        </div>
    );
});

export default Repere2DInteractiveEleve;
